import React, { useCallback, useEffect, useState } from "react";
import { getCallParams, makeCall } from "../../utils/service";
import urls from "../../global/constants/UrlConstants";
import history from "../../utils/history";
import RecordingCell from "../CallLogs/components/ShowAll/RecordingCell";
import {
  formatPhoneKeepingCountryCode,
  resolveLeadPhoneForDisplay,
} from "../../Shared/Phone/PhoneWithCountryCode";
import { store } from "../../utils/store";

type LeadMatch = {
  leadId?: string;
  leadName?: string;
  assignedUserName?: string;
  lastCallAt?: string;
  lastCallStatus?: string;
};

type ActiveCall = {
  _id: string;
  status: string;
  agentName?: string;
  customerNumber?: string;
  leadName?: string;
  durationFormatted?: string;
  phoneMasked?: boolean;
  canViewFullPhone?: boolean;
  inboundMatchStatus?: string;
  leadMatches?: LeadMatch[];
  preferredAssigneeName?: string;
  direction?: string;
};

type CallLogRow = {
  _id: string;
  leadName?: string;
  agentName?: string;
  callerNumber?: string;
  durationFormatted?: string;
  callDurationSeconds?: number;
  callStatus?: string;
  recordingFileName?: string;
  recordingUrl?: string;
  storedRecordingKey?: string;
  storedRecordingUrl?: string;
  recordingStoreStatus?: string;
  recordingStoreError?: string;
  recordingStatus?: string;
  recordingError?: string;
  phoneMasked?: boolean;
  canViewFullPhone?: boolean;
  leadPhone?: string;
  leadPhoneMasked?: string;
  inboundMatchStatus?: string;
  leadMatches?: LeadMatch[];
  preferredAssigneeName?: string;
  direction?: string;
};

function displayCallPhone(row: {
  callerNumber?: string;
  customerNumber?: string;
  leadPhone?: string;
  leadPhoneMasked?: string;
  phoneMasked?: boolean;
  canViewFullPhone?: boolean;
}) {
  const auth = store.getState()?.auth || {};
  const isOrgAdmin = String(auth.hierarchyLevel ?? "") === "1";
  if (row.canViewFullPhone === true || (isOrgAdmin && row.phoneMasked !== true)) {
    return formatPhoneKeepingCountryCode(
      row.callerNumber || row.customerNumber || row.leadPhone || ""
    );
  }
  return formatPhoneKeepingCountryCode(
    resolveLeadPhoneForDisplay({
      leadPhone: row.callerNumber || row.customerNumber || row.leadPhone,
      leadPhoneMasked: row.leadPhoneMasked || row.callerNumber,
      phoneMasked: row.phoneMasked !== false,
      canViewFullPhone: false,
    })
  );
}

const statusLabel: Record<string, string> = {
  initiated: "Connecting",
  dialing: "Dialing",
  ringing: "Ringing",
  answered: "On call",
};

function MatchHint({
  leadName,
  inboundMatchStatus,
  leadMatches,
  preferredAssigneeName,
}: {
  leadName?: string;
  inboundMatchStatus?: string;
  leadMatches?: LeadMatch[];
  preferredAssigneeName?: string;
}) {
  const status = String(inboundMatchStatus || "").toLowerCase();
  const matches = Array.isArray(leadMatches) ? leadMatches : [];

  if (status === "unknown" || leadName === "Unknown number") {
    return (
      <p className="mt-0.5 text-[11px] text-amber-700">
        Unknown number — create a lead from Call Logs if needed
      </p>
    );
  }
  if (status === "multiple" && matches.length > 1) {
    return (
      <p className="mt-0.5 text-[11px] text-[#6B7280]">
        {matches.length} matching leads
        {preferredAssigneeName
          ? ` · prefer ${preferredAssigneeName}`
          : ""}
        {matches[0]?.lastCallAt
          ? ` · last call ${new Date(matches[0].lastCallAt).toLocaleString()}`
          : ""}
      </p>
    );
  }
  if (matches[0]?.assignedUserName || preferredAssigneeName) {
    return (
      <p className="mt-0.5 text-[11px] text-[#6B7280]">
        Assigned: {preferredAssigneeName || matches[0]?.assignedUserName}
        {matches[0]?.lastCallStatus
          ? ` · last: ${matches[0].lastCallStatus}`
          : ""}
      </p>
    );
  }
  return null;
}

/**
 * Admin/team dashboard strip: live IVR + recent call logs with recordings.
 */
export default function DashboardIvrPanel() {
  const [live, setLive] = useState<ActiveCall[]>([]);
  const [recent, setRecent] = useState<CallLogRow[]>([]);

  const load = useCallback(async () => {
    try {
      const params = await getCallParams("GET");
      const [activeRes, logsRes] = await Promise.all([
        makeCall(`${urls.ACTIVECALLS}?scope=team`, params),
        makeCall(
          `${urls.CALLLOGDATA}?page=0&size=5`,
          params
        ),
      ]);
      if (activeRes?.success === 1) {
        setLive(Array.isArray(activeRes.data) ? activeRes.data : []);
      }
      if (logsRes?.success === 1) {
        setRecent(Array.isArray(logsRes.data) ? logsRes.data : []);
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    // One-shot on mount (recent logs + any live). Continuous poll only while live.
    load();
  }, [load]);

  useEffect(() => {
    if (!live.length) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [live.length, load]);

  if (!live.length && !recent.length) return null;

  return (
    <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-12">
      {live.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-[#F3D4DE] bg-[#FFF8FA] shadow-[0_4px_20px_rgba(15,23,42,0.04)] xl:col-span-4">
          <div className="flex items-center justify-between border-b border-[#F3D4DE] px-5 py-3.5">
            <h3 className="text-[15px] font-bold text-[#841C39]">
              Live IVR ({live.length})
            </h3>
            <button
              type="button"
              className="text-xs font-medium text-[#841C39] hover:underline"
              onClick={() => history.push(urls.teamViewPath)}
            >
              Team
            </button>
          </div>
          <ul className="divide-y divide-[#F3D4DE]">
            {live.slice(0, 5).map((c) => (
              <li
                key={c._id}
                className="flex items-center justify-between gap-2 px-5 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#111827]">
                    {c.agentName || "Agent"} →{" "}
                    {c.leadName || displayCallPhone(c)}
                  </p>
                  <MatchHint
                    leadName={c.leadName}
                    inboundMatchStatus={c.inboundMatchStatus}
                    leadMatches={c.leadMatches}
                    preferredAssigneeName={c.preferredAssigneeName}
                  />
                  <p className="text-xs text-[#6B7280]">
                    <span
                      className={`mr-1 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                        c.status === "answered"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {statusLabel[c.status] || c.status}
                    </span>
                    {c.direction === "inbound" ? (
                      <span className="text-[10px] text-[#841C39]">Inbound</span>
                    ) : null}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-bold tabular-nums text-[#841C39]">
                  {c.durationFormatted || "0:00"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        className={`overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)] ${
          live.length ? "xl:col-span-8" : "xl:col-span-12"
        }`}
      >
        <div className="flex items-center justify-between border-b border-[#EEF0F5] px-5 py-3.5">
          <h3 className="text-[15px] font-bold text-[#111827]">
            Recent IVR calls
          </h3>
          <button
            type="button"
            className="text-xs font-medium text-[#841C39] hover:underline"
            onClick={() => history.push(urls.callLogs)}
          >
            All call logs
          </button>
        </div>
        {!recent.length ? (
          <p className="px-5 py-6 text-sm text-[#9CA3AF]">No call logs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[#EEF0F5] text-xs text-[#6B7280]">
                  <th className="px-5 py-2 font-medium">Lead / Number</th>
                  <th className="px-3 py-2 font-medium">Agent</th>
                  <th className="px-3 py-2 font-medium">Duration</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-5 py-2 font-medium">Recording</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((row) => (
                  <tr key={row._id} className="border-b border-[#F3F4F6]">
                    <td className="px-5 py-2.5">
                      <div className="font-medium text-[#111827]">
                        {row.leadName || "—"}
                      </div>
                      <div className="text-xs text-[#6B7280]">
                        {displayCallPhone(row)}
                      </div>
                      <MatchHint
                        leadName={row.leadName}
                        inboundMatchStatus={row.inboundMatchStatus}
                        leadMatches={row.leadMatches}
                        preferredAssigneeName={row.preferredAssigneeName}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-[#374151]">
                      {row.agentName ||
                        row.preferredAssigneeName ||
                        "—"}
                    </td>
                    <td className="px-3 py-2.5 tabular-nums text-[#374151]">
                      {row.durationFormatted ||
                        (row.callDurationSeconds != null
                          ? `${row.callDurationSeconds}s`
                          : "—")}
                    </td>
                    <td className="px-3 py-2.5 text-[#374151]">
                      {row.callStatus || "—"}
                    </td>
                    <td className="px-5 py-2.5">
                      <RecordingCell
                        fileName={row.recordingFileName}
                        recordingUrl={row.recordingUrl}
                        storedRecordingUrl={row.storedRecordingUrl}
                        callLogId={row._id}
                        durationLabel={row.durationFormatted}
                        recordingStatus={row.recordingStatus}
                        recordingError={row.recordingError}
                        recordingStoreStatus={row.recordingStoreStatus}
                        recordingStoreError={row.recordingStoreError}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
